import axios from "axios";

const isDevelopment = process.env.NODE_ENV === "development";
const explicitApiUrl = process.env.REACT_APP_API_URL || "";
const explicitBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const browserOrigin =
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "";

const parseHostname = (value) => {
  try {
    return new URL(value, browserOrigin || "http://localhost").hostname.toLowerCase();
  } catch (_) {
    return "";
  }
};

const storefrontHosts = new Set(["eclipsetenis.com", "www.eclipsetenis.com"]);
const loopbackHosts = new Set(["localhost", "127.0.0.1"]);

let rawBase = explicitApiUrl || explicitBackendUrl || browserOrigin || "http://localhost:8000";

// En desarrollo local, evita que una URL de produccion rompa el login/admin.
if (isDevelopment && /eclipsetenis\.com/i.test(rawBase)) {
  rawBase = explicitBackendUrl || "http://localhost:8000";
}

// En produccion, usa el mismo host/origen desde donde se abrio la tienda.
// Evita fallos de CORS o mezcla apex/www cuando el bundle tiene una URL fija.
if (!isDevelopment && browserOrigin) {
  const configuredHost = parseHostname(rawBase);
  const currentHost = parseHostname(browserOrigin);
  if (
    !configuredHost ||
    configuredHost === currentHost ||
    storefrontHosts.has(configuredHost) ||
    loopbackHosts.has(configuredHost)
  ) {
    rawBase = browserOrigin;
  }
}

const API_BASE = rawBase.endsWith("/api")
  ? rawBase
  : `${rawBase.replace(/\/$/, "")}/api`;

export const API_BASE_URL = API_BASE;

const api = axios.create({
  baseURL: API_BASE,
});

const authHeaders = (token) =>
  token ? { Authorization: `Bearer ${token}` } : {};

export const getProducts = async (params = {}) => {
  const response = await api.get("/products", { params });
  return response.data;
};

export const getProductFacets = async (params = {}) => {
  const response = await api.get("/products/facets", { params });
  return response.data;
};

export const getCategories = async () => {
  const response = await api.get("/categories");
  return response.data;
};

export const getProduct = async (id) => {
  const response = await api.get(`/products/${id}`);
  return response.data;
};

export const adminLogin = async (username, password) => {
  const response = await api.post("/admin/login", { username, password });
  return response.data;
};

export const adminMe = async (token) => {
  const response = await api.get("/admin/me", { headers: authHeaders(token) });
  return response.data;
};

export const changeAdminPassword = async (payload, token) => {
  const response = await api.put("/admin/change-password", payload, {
    headers: authHeaders(token),
  });
  return response.data;
};

export const createProduct = async (formData, token) => {
  const response = await api.post("/admin/products", formData, {
    headers: {
      ...authHeaders(token),
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

export const updateProduct = async (id, formData, token) => {
  const response = await api.put(`/admin/products/${id}`, formData, {
    headers: {
      ...authHeaders(token),
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

export const deleteProduct = async (id, token) => {
  const response = await api.delete(`/admin/products/${id}`, {
    headers: authHeaders(token),
  });
  return response.data;
};

export const createOrder = async (payload) => {
  const response = await api.post("/orders", payload);
  return response.data;
};

export const getVendors = async () => {
  const response = await api.get("/vendors");
  return response.data;
};

export const subscribeNewsletter = async (payload) => {
  const response = await api.post("/newsletter/subscribe", payload);
  return response.data;
};

export const getReviews = async () => {
  const response = await api.get("/reviews");
  return response.data;
};

export const createReview = async (payload) => {
  const response = await api.post("/reviews", payload);
  return response.data;
};

export const getAdminNewsletterSubscribers = async (token) => {
  const response = await api.get("/admin/newsletter/subscribers", {
    headers: authHeaders(token),
  });
  return response.data;
};

export const getAdminOrders = async (token) => {
  const response = await api.get("/admin/orders", {
    headers: authHeaders(token),
  });
  return response.data;
};

export const getAdminReviews = async (token) => {
  const response = await api.get("/admin/reviews", {
    headers: authHeaders(token),
  });
  return response.data;
};

export const deleteAdminReview = async (reviewId, token) => {
  const response = await api.delete(`/admin/reviews/${reviewId}`, {
    headers: authHeaders(token),
  });
  return response.data;
};

export const getAdminVendors = async (token) => {
  const response = await api.get("/admin/vendors", { headers: authHeaders(token) });
  return response.data;
};

export const getAdminCategories = async (token) => {
  const response = await api.get("/admin/categories", { headers: authHeaders(token) });
  return response.data;
};

export const getAdminProductColors = async (token) => {
  const response = await api.get("/admin/product-colors", {
    headers: authHeaders(token),
  });
  return response.data;
};

export const getAdminProductBrands = async (token) => {
  const response = await api.get("/admin/product-brands", {
    headers: authHeaders(token),
  });
  return response.data;
};

export const getAdminProductSizes = async (token, params = {}) => {
  const response = await api.get("/admin/product-sizes", {
    headers: authHeaders(token),
    params,
  });
  return response.data;
};

export const deleteAdminProductSize = async (token, params = {}) => {
  const response = await api.delete("/admin/product-sizes", {
    headers: authHeaders(token),
    params,
  });
  return response.data;
};

export const createAdminCategory = async (payload, token) => {
  const response = await api.post("/admin/categories", payload, {
    headers: {
      ...authHeaders(token),
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

export const updateAdminCategory = async (id, payload, token) => {
  const response = await api.put(`/admin/categories/${id}`, payload, {
    headers: {
      ...authHeaders(token),
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

export const deleteAdminCategory = async (id, token) => {
  const response = await api.delete(`/admin/categories/${id}`, {
    headers: authHeaders(token),
  });
  return response.data;
};

export const createVendor = async (payload, token) => {
  const response = await api.post("/admin/vendors", payload, { headers: authHeaders(token) });
  return response.data;
};

export const getAdminVendorWhatsappTemplates = async (token) => {
  const response = await api.get("/admin/vendors/whatsapp/templates", {
    headers: authHeaders(token),
  });
  return response.data;
};

export const createAdminVendorWhatsappTemplate = async (payload, token) => {
  const response = await api.post("/admin/vendors/whatsapp/templates", payload, {
    headers: authHeaders(token),
  });
  return response.data;
};

export const updateAdminVendorWhatsappTemplate = async (id, payload, token) => {
  const response = await api.put(`/admin/vendors/whatsapp/templates/${id}`, payload, {
    headers: authHeaders(token),
  });
  return response.data;
};

export const deleteAdminVendorWhatsappTemplate = async (id, token) => {
  const response = await api.delete(`/admin/vendors/whatsapp/templates/${id}`, {
    headers: authHeaders(token),
  });
  return response.data;
};

export const deleteVendor = async (id, token) => {
  const response = await api.delete(`/admin/vendors/${id}`, { headers: authHeaders(token) });
  return response.data;
};

export const sendVendorWelcomeEmail = async (payload, token) => {
  const response = await api.post("/admin/vendors/email/welcome", payload, {
    headers: authHeaders(token),
  });
  return response.data;
};

export const getAdminCoupons = async (token) => {
  const response = await api.get("/admin/coupons", { headers: authHeaders(token) });
  return response.data;
};

export const createAdminCoupon = async (payload, token) => {
  const response = await api.post("/admin/coupons", payload, { headers: authHeaders(token) });
  return response.data;
};

export const disableAdminCoupon = async (id, token) => {
  const response = await api.delete(`/admin/coupons/${id}`, { headers: authHeaders(token) });
  return response.data;
};

export const validateCoupon = async (payload) => {
  const response = await api.post("/coupons/validate", payload);
  return response.data;
};

export const getOrderStatus = async (orderCode) => {
  const response = await api.get(`/orders/${orderCode}`);
  return response.data;
};

export const getTermsContent = async () => {
  const response = await api.get("/content/terms");
  return response.data;
};

export const updateTermsContent = async (content, token) => {
  const response = await api.put(
    "/admin/content/terms",
    { content },
    { headers: authHeaders(token) },
  );
  return response.data;
};

export const getPrivacyPolicyContent = async () => {
  const response = await api.get("/content/privacy-policy");
  return response.data;
};

export const updatePrivacyPolicyContent = async (content, token) => {
  const response = await api.put(
    "/admin/content/privacy-policy",
    { content },
    { headers: authHeaders(token) },
  );
  return response.data;
};

export const getPurchaseMessageContent = async () => {
  const response = await api.get("/content/purchase-message");
  return response.data;
};

export const updatePurchaseMessageContent = async (content, token) => {
  const response = await api.put(
    "/admin/content/purchase-message",
    { content },
    { headers: authHeaders(token) },
  );
  return response.data;
};

export const getHomeSettings = async () => {
  const response = await api.get("/content/home-settings");
  return response.data;
};

export const updateHomeSettings = async (formData, token) => {
  const response = await api.put("/admin/content/home-settings", formData, {
    headers: {
      ...authHeaders(token),
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};
